import type { CSSProperties, ReactNode } from 'react'
import './olivium-governance.css'
import type { GovvAccent } from './governanceCards'
import OliviumCommandBar from './OliviumCommandBar'

/** Resolve a governance accent name to its scoped CSS variable. */
export function accentVar(accent: GovvAccent): string {
  return `var(--govv-${accent})`
}

export interface Breadcrumb {
  label: string
  to?: string
}

export interface OliviumSuiteShellProps {
  title: string
  subtitle?: string
  accent?: GovvAccent
  /** Tooltip text for the honesty badge — what is durable vs local-session here. */
  honesty: string
  breadcrumb?: Breadcrumb[]
  /** Rendered at the right of the header (actions, search launcher, etc.). */
  rightSlot?: ReactNode
  onHome: () => void
  onBack?: () => void
  onOpenPath?: (to: string) => void
  children: ReactNode
}

const headerStyle: CSSProperties = {
  position: 'sticky', top: 0, zIndex: 20,
  display: 'flex', alignItems: 'center', gap: 16,
  padding: '14px 22px',
  borderBottom: '1px solid var(--govv-line)',
  background: 'color-mix(in srgb, var(--govv-canvas) 88%, transparent)',
  backdropFilter: 'blur(12px)',
}

/**
 * OliviumSuiteShell — the shared chrome for every Olivium governance surface.
 *
 * Presentational: navigation is delegated through callbacks so the route
 * wrapper owns `useNavigate`. Renders the `.olv-gov` scoped root (tokens +
 * base styles), the Olivium identity header, a breadcrumb, and the honesty
 * badge whose tooltip states what is durable vs. local-session on this screen.
 */
export default function OliviumSuiteShell({
  title, subtitle, accent = 'identity', honesty, breadcrumb, rightSlot,
  onHome, onBack, onOpenPath, children,
}: OliviumSuiteShellProps) {
  return (
    <div className="olv-gov" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="olv-gov-header" style={headerStyle} role="banner">
        <button
          type="button"
          className="olv-gov-btn"
          onClick={onHome}
          aria-label="Olivium suite home"
          style={{ padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <span aria-hidden style={{ color: 'var(--govv-identity)', fontFamily: 'var(--sc-font-display)', fontWeight: 700 }}>Olivium</span>
        </button>

        <nav aria-label="Breadcrumb" style={{ flex: 1, minWidth: 0 }}>
          <ol style={{ listStyle: 'none', display: 'flex', alignItems: 'center', gap: 8, margin: 0, padding: 0, flexWrap: 'wrap' }}>
            {(breadcrumb ?? []).map((crumb, i) => (
              <li key={`${crumb.label}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {i > 0 && <span aria-hidden style={{ color: 'var(--govv-ink-3)' }}>/</span>}
                {crumb.to && onOpenPath ? (
                  <button
                    type="button"
                    onClick={() => onOpenPath(crumb.to!)}
                    className="olv-gov-btn"
                    style={{ padding: '2px 6px', background: 'transparent', border: 'none', color: 'var(--govv-ink-2)', fontWeight: 500 }}
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span aria-current="page" style={{ color: 'var(--govv-ink)', fontWeight: 600, fontSize: 13 }}>{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <OliviumCommandBar onOpenPath={onOpenPath} />
        <span
          className="olv-gov-honesty"
          title={honesty}
          aria-label={`Honesty note: ${honesty}`}
        >
          Links · Not Faked
        </span>
        {rightSlot}
      </header>

      <main role="main" style={{ padding: '22px', flex: 1 }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
            <div style={{ minWidth: 0 }}>
              <h1 style={{
                margin: 0, fontFamily: 'var(--sc-font-display)', fontWeight: 700,
                fontSize: 26, lineHeight: 1.15, color: accentVar(accent),
              }}>
                {title}
              </h1>
              {subtitle && (
                <p style={{ margin: '6px 0 0', color: 'var(--govv-ink-2)', fontSize: 14, maxWidth: 720 }}>{subtitle}</p>
              )}
            </div>
            {onBack && (
              <button type="button" className="olv-gov-btn" onClick={onBack} style={{ flexShrink: 0 }}>
                ← Back
              </button>
            )}
          </div>
          {children}
        </div>
      </main>
    </div>
  )
}
